"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";
// import DeliveryProcessMobile from "./delivery-process-mobile";
import DeliveryProcessMobileNew from "./delivery-process-mobile-new";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

// The badge/connector for each step sits 25px below its own card's top edge.
const BADGE_OFFSET_FROM_CARD_TOP = 25;

interface DeliveryProcessProps {
  data?: any;
}

interface DeliveryProcessTab {
  id: number;
  heading: string;
  description: string;
  icon?: string;
}

function DeliveryProcessCard({
  tab,
  cardRef,
}: {
  tab: DeliveryProcessTab;
  cardRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={cardRef}
      className="relative min-h-[270px] max-h-[296px] min-[1440px]:min-h-[285px] min-[1440px]:max-h-[315px] overflow-hidden bg-gradient-to-br from-[#EAF5FD] to-[#DCEEFB] xl:p-[32px] p-[24px] shadow-[0_10px_30px_0_#0086E60F]"
    >
      {/* Card background */}
      <Image
        src="/images/clutch-landing/card-bg.png" 
        alt=""
        fill
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />

      {/* Bottom-right decorative block */}
      <Image
        src="/images/clutch-landing/card-bg-block.png"
        alt=""
        height={111}
        width={45}
        className="pointer-events-none absolute bottom-0 right-0"
      />

      <span className="absolute left-[10px] top-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
      <span className="absolute right-[10px] top-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
      <span className="absolute bottom-[10px] left-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />
      <span className="absolute bottom-[10px] right-[10px] h-[8px] w-[8px] rounded-full bg-white shadow-[0_3px_6px_0_#00000033]" />

      {/* Your card content */}
      {tab.icon && (
        <div className="relative mb-[14px] xl:h-[56px] xl:w-[56px] h-[46px] w-[46px]">
          <Image
            src={tab.icon}
            alt={tab.heading}
            fill
            sizes="36px"
            className="object-contain"
          />
        </div>
      )}

      <h3 className="mb-[10px] xl:text-[24px] font-bold xl:leading-[30px] leading-[26px] text-[20px] text-black line-clamp-2">
        {tab.heading}
      </h3>
      <p className="xl:text-[18px] xl:leading-[24px] text-[16px] xl:leading-[22.4px] text-secondary-text line-clamp-6 relative">
        {tab.description}
      </p>
    </div>
  );
}

export default function DeliveryProcess({ data }: DeliveryProcessProps) {
  const tabs: DeliveryProcessTab[] = (data?.slides || []).map((slide: any) => {
    const iconUrl =
      slide?.image?.data?.attributes?.url ||
      slide?.ipad_image?.data?.attributes?.url ||
      slide?.mobile_image?.data?.attributes?.url ||
      slide?.hover_image?.data?.attributes?.url;

    return {
      id: slide.id,
      heading: slide.display_title,
      description: slide.title,
      icon: iconUrl ? toWebp(`${baseUrl}${iconUrl}`) : undefined,
    };
  });

  // Written straight to the DOM: as state it re-rendered the whole section on every scroll frame
  const lineFillRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [badgeTops, setBadgeTops] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const activationPoint = window.innerHeight / 2;
      const progress = activationPoint - rect.top;
      if (lineFillRef.current)
        lineFillRef.current.style.height = `${Math.max(0, Math.min(rect.height, progress))}px`;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tabs.length]);

  // Badges/connectors are pinned 25px below each card's own top edge, not a
  // fixed step formula — card heights vary (line-clamped copy, responsive
  // clamp() sizing), so only measuring the real DOM position stays in sync.
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      if (!container) return;
      const containerTop = container.getBoundingClientRect().top;

      setBadgeTops(
        cardRefs.current.map((card) =>
          card
            ? card.getBoundingClientRect().top -
              containerTop +
              BADGE_OFFSET_FROM_CARD_TOP
            : 0,
        ),
      );
    };

    measure();
    window.addEventListener("resize", measure);
    const lateMeasure = window.setTimeout(measure, 500);
    return () => {
      window.removeEventListener("resize", measure);
      window.clearTimeout(lateMeasure);
    };
  }, [tabs.length]);

  if (!tabs.length) return null;

  const indexedTabs = tabs.map((tab, index) => ({ tab, index }));
  const leftTabs = indexedTabs.filter(({ index }) => index % 2 === 0);
  const rightTabs = indexedTabs.filter(({ index }) => index % 2 === 1);

  return (
    <>
      <div className="relative overflow-hidden lg:max-w-[1240px] xl:p-[100px_20px_0_24px] lg:p-[80px_20px_0_24px] sm:p-[60px_20px_0_24px] mx-auto w-full bg-white lg:block hidden">
        <div className="mx-auto w-full ">
          <h2 className="mx-auto w-full xl:max-w-[963px] md:max-w-[863px] max-w-[663px] text-center font-bold xl:mb-[40px] mb-[32px] xl:text-[56px] xl:leading-[72.8px] md:text-[40px] md:leading-[52px] text-[30px] leading-[39px] text-black">
            {data?.title}
          </h2>
        </div>

        <section className="relative overflow-hidden bg-white lg:pt-[60px] pt-[50px]">
          <div
            ref={containerRef}
            className="relative z-[1] mx-auto flex max-w-[858px] min-[1280px]:max-w-[1192px] gap-x-[64px] min-[1440px]:gap-x-[80px] lg:[--dp-step:155px] min-[1440px]:[--dp-step:167px] "
          >
            {/* Connector line + step badges */}
            <div className="pointer-events-none absolute inset-0 z-0">
              {/* Base line fills the overlay's full height, which — via
                  `inset-0` on this wrapper — always matches the taller of
                  the two staggered columns, so it naturally ends at the
                  bottom of the last card instead of a step-count guess. */}
              <div
                ref={trackRef}
                className="absolute left-1/2 top-[-50px] h-[120%] w-[2px] -translate-x-1/2 bg-[#B9DDF6]"
              />
              <div
                className="absolute left-1/2 top-[-50px] w-[1px] -translate-x-1/2 bg-gradient-to-b from-[#149dff] to-[#0086E6]"
                ref={lineFillRef}
                style={{ height: 0 }}
              />
              {badgeTops.length === tabs.length &&
                tabs.map((tab, index) => {
                  const isLeft = index % 2 === 0;
                  const top = `${badgeTops[index]}px`;
                  return (
                    <div key={tab.id ?? index}>
                      {/* Short tick connecting the badge to its card's side */}
                      <div
                        className={`absolute h-[2px] w-[32px] -translate-y-1/2 bg-[#B9DDF6] min-[1440px]:w-[40px] ${
                          isLeft
                            ? "right-[calc(50%_+_22px)]"
                            : "left-[calc(50%_+_22px)]"
                        }`}
                        style={{ top }}
                      />
                      <div
                        className="absolute left-1/2 flex h-[44px] w-[44px] -translate-x-1/2 -translate-y-1/2 items-center justify-center border border-[#B9DDF6] bg-white text-[15px] font-bold text-primary-text shadow-[0_4px_10px_0_#00000014]"
                        style={{ top }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Left column */}
            <div className="relative z-[1] flex w-1/2 flex-col gap-y-[48px] min-[1440px]:gap-y-[99px] mr-[40px]">
              {leftTabs.map(({ tab, index }) => (
                <DeliveryProcessCard
                  key={tab.id}
                  tab={tab}
                  cardRef={(el) => (cardRefs.current[index] = el)}
                />
              ))}
            </div>

            {/* Right column, staggered halfway down */}
            <div className="relative z-[1] flex w-1/2 flex-col gap-y-[48px] min-[1440px]:gap-y-[99px] lg:mt-[var(--dp-step)]">
              {rightTabs.map(({ tab, index }) => (
                <DeliveryProcessCard
                  key={tab.id}
                  tab={tab}
                  cardRef={(el) => (cardRefs.current[index] = el)}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* <DeliveryProcessMobile data={data} /> */}
      <DeliveryProcessMobileNew data={data} />
    </>
  );
}
